import { Link } from 'react-router-dom';
import { Settings, Phone, CheckCircle, Calendar, FileText, Shield } from 'lucide-react';
import SEO from '../components/SEO';

const Entretien = () => {
  const contractTypes = [
    {
      icon: <Calendar size={28} />,
      title: 'Entretien Périodique',
      description: 'Interventions planifiées selon les heures de fonctionnement de votre engin.',
      features: ['Vidange huile moteur', 'Remplacement filtres', 'Graissage complet', 'Contrôle niveaux']
    },
    {
      icon: <Shield size={28} />,
      title: 'Contrat Annuel',
      description: 'Formule tout compris pour une tranquillité d\'esprit maximale.',
      features: ['Visites illimitées', 'Pièces d\'usure incluses', 'Priorité d\'intervention', 'Rapport d\'état annuel']
    },
    {
      icon: <FileText size={28} />,
      title: 'Sur Demande',
      description: 'Intervention ponctuelle selon vos besoins spécifiques.',
      features: ['Devis gratuit', 'Intervention rapide', 'Pièces certifiées', 'Garantie travaux']
    }
  ];

  const checklist = [
    { category: 'Moteur', items: ['Niveau et état de l\'huile', 'Filtres (air, huile, carburant)', 'Courroies et durites', 'Système de refroidissement'] },
    { category: 'Hydraulique', items: ['Niveau huile hydraulique', 'État des flexibles', 'Vérins et pompes', 'Filtres hydrauliques'] },
    { category: 'Transmission', items: ['Huile de transmission', 'État de l\'embrayage', 'Réducteurs', 'Chenilles ou pneus'] },
    { category: 'Électrique', items: ['Batterie et connexions', 'Éclairage', 'Capteurs et alarmes', 'Tableau de bord'] },
    { category: 'Sécurité', items: ['Freins de service', 'Frein de parking', 'Structure ROPS/FOPS', 'Extincteur'] },
    { category: 'Carrosserie', items: ['État de la peinture', 'Points de corrosion', 'Vitrerie cabine', 'Rétroviseurs'] }
  ];

  const benefits = [
    { title: 'Durée de Vie Prolongée', description: 'Un entretien régulier peut doubler la durée de vie de votre engin.' },
    { title: 'Économies', description: 'Prévenir les pannes coûte moins cher que les réparer.' },
    { title: 'Sécurité', description: 'Garantissez la sécurité de vos opérateurs.' },
    { title: 'Valeur de Revente', description: 'Un engin bien entretenu conserve sa valeur.' }
  ];

  return (
    <div data-testid="entretien-page">
      <SEO
        title="Entretien Préventif Engins de Chantier La Réunion - Contrats Maintenance"
        description="Entretien préventif et contrats de maintenance pour engins de chantier à La Réunion. Réduisez vos pannes de 50%. Vidange moteur, filtres, graissage, contrôle hydraulique. Micro-pelles, pelles, tracto-pelles. Devis gratuit — IDMAT Service Saint-Leu."
        canonical="/entretien"
        image="https://images.pexels.com/photos/5125783/pexels-photo-5125783.jpeg?auto=compress&cs=tinysrgb&w=1200"
      />
      {/* Hero Section */}
      <section className="hero-section hero-entretien min-h-[60vh]">
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl animate-slide-up">
            <span 
              className="text-sm font-bold tracking-widest uppercase text-[#F5C500] block mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Notre Service
            </span>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Entretien Préventif
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Prévenez les pannes et prolongez la durée de vie de vos engins 
              avec nos programmes d'entretien sur mesure.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="entretien-cta">
              Demander un Devis
            </Link>
          </div>
        </div>
      </section>

      {/* Contract Types Section */}
      <section className="section-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span 
              className="text-sm font-bold tracking-widest uppercase text-[#F5C500] block mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Nos Formules
            </span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Contrats d'Entretien
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Choisissez la formule adaptée à vos besoins et à l'utilisation de vos engins.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contractTypes.map((contract, index) => (
              <div 
                key={index} 
                className="service-card bg-white shadow-lg p-8 flex flex-col"
                data-testid={`contract-type-${index}`}
              >
                <div className="icon-container mb-6">
                  {contract.icon}
                </div>
                <h3 
                  className="text-xl font-bold text-[#1a1a1a] mb-3"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {contract.title}
                </h3>
                <p className="text-gray-600 mb-6">{contract.description}</p>
                <ul className="space-y-2 mt-auto">
                  {contract.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-700">
                      <CheckCircle size={18} className="text-[#F5C500]" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Checklist Section */}
      <section className="section-gray py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span 
              className="text-sm font-bold tracking-widest uppercase text-[#F5C500] block mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Check-list
            </span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Points de Contrôle
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Lors de chaque entretien, nos techniciens vérifient l'ensemble de ces points.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {checklist.map((section, index) => (
              <div 
                key={index} 
                className="bg-white p-6 border-l-4 border-[#F5C500]"
                data-testid={`checklist-${index}`}
              >
                <h3 
                  className="text-lg font-bold text-[#1a1a1a] mb-4"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {section.category}
                </h3>
                <ul className="space-y-2">
                  {section.items.map((item, idx) => (
                    <li key={idx} className="checklist-item !p-3 !mb-2">
                      <div className="check-icon !w-5 !h-5">
                        <CheckCircle size={12} color="#1a1a1a" />
                      </div>
                      <span className="text-gray-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span 
                className="text-sm font-bold tracking-widest uppercase text-[#F5C500] block mb-4"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Avantages
              </span>
              <h2 
                className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-8"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Pourquoi Entretenir Régulièrement ?
              </h2>
              <div className="space-y-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#F5C500] flex items-center justify-center flex-shrink-0">
                      <CheckCircle size={24} color="#1a1a1a" />
                    </div>
                    <div>
                      <h4 
                        className="font-bold text-[#1a1a1a] mb-1"
                        style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                      >
                        {benefit.title}
                      </h4>
                      <p className="text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/5125783/pexels-photo-5125783.jpeg?auto=compress&cs=tinysrgb&w=800" 
                alt="Entretien engin"
                className="w-full shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#F5C500] p-6 shadow-xl hidden md:block">
                <span 
                  className="text-4xl font-bold text-[#1a1a1a] block"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  -50%
                </span>
                <span className="text-[#1a1a1a] font-medium text-sm">de pannes avec<br />entretien régulier</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1a1a1a] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Planifiez Votre Entretien
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            N'attendez pas la panne ! Contactez-nous pour établir un programme d'entretien adapté à vos engins.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Demander un Devis
            </Link>
            <a href="tel:0693526449" className="btn-secondary flex items-center justify-center gap-2">
              <Phone size={20} />
              06 93 52 64 49
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Entretien;
